import {
  useOrganicUnderperformersQuery,
  useWebsiteTrafficTopPerformersQuery,
} from '../api/queries';
import { formatNumber } from '../lib/format';
import type {
  OrganicUnderperformer,
  WebsiteTrafficTopPerformer,
} from '../types';

type Props = {
  onDrillIn: (afNumber: string) => void;
};

export default function WebsiteTrafficSection({ onDrillIn }: Props) {
  const topQ = useWebsiteTrafficTopPerformersQuery(true);
  const needsQ = useOrganicUnderperformersQuery(true);

  const top = topQ.data ?? [];
  const needs = needsQ.data ?? [];
  if (!topQ.isLoading && !needsQ.isLoading && top.length === 0 && needs.length === 0) {
    return null;
  }

  return (
    <div className="ds-reviews-row">
      <div className="ds-reviews-col">
        <div className="ds-section-header">
          <h2>Website Traffic — Top Performers</h2>
          <span className="meta">
            {topQ.isLoading
              ? 'Loading…'
              : topQ.error
                ? `Failed to load: ${(topQ.error as Error).message}`
                : `Top ${top.length} franchisees by sessions in the last 90 days`}
          </span>
        </div>
        <div className="ds-panel ds-reviews-panel ds-reviews-panel--table">
          {top.length > 0 ? (
            <table className="ds-table ds-table-compact">
              <thead>
                <tr>
                  <th style={{ width: 28 }}></th>
                  <th>Franchisee</th>
                  <th className="num">Sessions (90d)</th>
                  <th>Open Date</th>
                </tr>
              </thead>
              <tbody>
                {top.map((r) => (
                  <TopRow key={r.afNumber} row={r} onDrillIn={onDrillIn} />
                ))}
              </tbody>
            </table>
          ) : (
            <div
              style={{
                padding: '40px 24px',
                textAlign: 'center',
                color: '#8a95a5',
                fontSize: 13,
              }}
            >
              {topQ.isLoading ? 'Loading…' : 'No traffic data yet.'}
            </div>
          )}
        </div>
      </div>

      <div className="ds-reviews-col">
        <div className="ds-section-header">
          <h2 style={{ color: '#dc2626' }}>Website Traffic — Needs Attention</h2>
          <span className="meta">
            {needsQ.isLoading
              ? 'Loading…'
              : needsQ.error
                ? `Failed to load: ${(needsQ.error as Error).message}`
                : `${needs.length} franchisee${needs.length === 1 ? '' : 's'} open 90+ days with fewer than 300 sessions in the last 90 days`}
          </span>
        </div>
        <div className="ds-panel ds-reviews-panel ds-reviews-panel--table">
          {needs.length > 0 ? (
            <table className="ds-table ds-table-compact">
              <thead>
                <tr>
                  <th style={{ width: 28 }}></th>
                  <th>Franchisee</th>
                  <th className="num">Sessions (90d)</th>
                  <th className="num">Days Open</th>
                  <th>Open Date</th>
                </tr>
              </thead>
              <tbody>
                {needs.map((r) => (
                  <NeedsRow key={r.afNumber} row={r} onDrillIn={onDrillIn} />
                ))}
              </tbody>
            </table>
          ) : (
            <div
              style={{
                padding: '40px 24px',
                textAlign: 'center',
                color: '#8a95a5',
                fontSize: 13,
              }}
            >
              {needsQ.isLoading ? 'Loading…' : 'Everyone is above the traffic floor.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TopRow({
  row,
  onDrillIn,
}: {
  row: WebsiteTrafficTopPerformer;
  onDrillIn: (af: string) => void;
}) {
  return (
    <tr onClick={() => onDrillIn(row.afNumber)}>
      <td>
        <span className="ds-marker green" />
      </td>
      <td>
        <div className="ds-name">{row.name}</div>
        <div className="ds-sub">{row.afNumber}</div>
      </td>
      <td className="num">
        <span className="num-val" style={{ color: '#15803d', fontWeight: 700 }}>
          {formatNumber(row.sessions)}
        </span>
      </td>
      <td style={{ fontSize: 12, color: '#5a6a7a' }}>{row.openDate}</td>
    </tr>
  );
}

function NeedsRow({
  row,
  onDrillIn,
}: {
  row: OrganicUnderperformer;
  onDrillIn: (af: string) => void;
}) {
  return (
    <tr onClick={() => onDrillIn(row.afNumber)}>
      <td>
        <span className="ds-marker red" />
      </td>
      <td>
        <div className="ds-name">{row.name}</div>
        <div className="ds-sub">{row.afNumber}</div>
      </td>
      <td className="num">
        <span className="num-val" style={{ color: '#dc2626', fontWeight: 700 }}>
          {formatNumber(row.sessions)}
        </span>
      </td>
      <td className="num">
        <span className="num-val">{row.daysOpen}</span>
      </td>
      <td style={{ fontSize: 12, color: '#5a6a7a' }}>{row.openDate}</td>
    </tr>
  );
}
